import {
  BaseService,
  type BaseServiceContext,
  createAppError,
  getAppErrorCause,
  isAppError,
} from '@bangle.io/base-utils';
import { SERVICE_NAME, WORKSPACE_STORAGE_TYPE } from '@bangle.io/constants';
import type {
  ActiveFrontmatter,
  EditorAction,
  EditorEngineContract,
} from '@bangle.io/context';
import {
  type EditorView,
  markdownLoader,
  type Schema,
  TextSelection,
  type Transaction,
} from '@bangle.io/prosemirror-plugins';
import {
  displayNameForAsset,
  type FileSystemService,
  type NavigationService,
  type NoteSnapshotService,
  type StoredMarkdownAsset,
  storeWorkspaceAssetFiles,
  type TextStats,
  type WorkbenchStateService,
  type WorkspaceOpsService,
  type WorkspaceStateService,
} from '@bangle.io/service-core';
import {
  type CommentThread,
  fetchProfilesByIds,
  getMyMembership,
  getUser,
  isSupabaseConfigured,
  listMembers,
  type MentionCandidate,
  normalizeMentionCandidate,
  observeComments,
  SupabaseFileYjsProvider,
} from '@bangle.io/supabase';
import type { Store } from '@bangle.io/types';
import { toast } from '@bangle.io/ui-components';
import {
  createMissingWikiLinkTarget,
  createWikiLinkIndex,
  getEmbeddableWorkspaceAssetKind,
  getInternalLinkHeading,
  normalizeLinkTarget,
  normalizeStoredMarkdownLinkTarget,
  relativeMarkdownAssetHref,
  resolveInternalLink,
  resolveWikiLinkTarget,
  resolveWorkspaceMarkdownAssetReferenceCandidates,
  type WikiLinkIndex,
  type WsFilePath,
  WsPath,
  workspaceRootMarkdownAssetHref,
} from '@bangle.io/ws-path';
import { atom } from 'jotai';
import {
  relativePositionToAbsolutePosition,
  ySyncPluginKey,
} from 'y-prosemirror';
import * as Y from 'yjs';
import type { MarkdownAssetReference } from './asset-file-plugin';
import {
  $collabCommentThreads,
  $collabPeers,
  $collabStatus,
  $collabYdoc,
  $pendingPresenceJump,
  abandonActiveCollabSession,
  getActiveCollabSession,
  setActiveCollabSession,
} from './collab-state';
import { setCommentThreads } from './doc-comment';
import {
  EDITOR_SAVE_DEBOUNCE_MS,
  type EditorSaveCoordinator,
  EditorSaveQueue,
} from './editor-save-queue';
import { setupExtensions } from './extensions';
import { ExternalContentSync } from './external-content-sync';
import {
  readFrontmatterTextFromDoc,
  replaceFrontmatterText,
} from './frontmatter-text';
import { findHeadingIndexBySlug } from './heading-slug';
import { $imageLightbox } from './image-lightbox-state';
import { createLocalImageNodeView } from './local-image-node-view';
import { createEditor, serializeEditorDocumentForSave } from './pm-setup';
import {
  getRememberedCursorPosition,
  resolveRememberedCursor,
} from './remembered-cursor';
import {
  collectLinkTargets,
  isMarkdownContentPreserved,
  isMarkdownRoundTripPreserved,
} from './round-trip-check';

const ASSET_TOAST_FILE_NAME_MAX_LENGTH = 44;

const EMPTY_WS_PATH_SET: ReadonlySet<string> = new Set();

export type PmEditorServiceConfig = {
  saveCoordinator: EditorSaveCoordinator;
  /**
   * When this returns false, mount/focus must not steal keyboard focus — e.g.
   * while the "name this note" dialog is open after creating a note.
   */
  canFocusEditor?: () => boolean;
};

/**
 * Stable per-path toast id so repeated refusals for the same note update one
 * toast instead of stacking, and a later successful reconciliation can
 * withdraw it.
 */
function staleExternalContentToastId(wsPath: string): string {
  return `external-stale-content:${wsPath}`;
}

function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 10 || unitIndex === 0 ? Math.round(value) : value.toFixed(1)} ${units[unitIndex]}`;
}

function truncateAssetToastFileName(fileName: string): string {
  const normalized = fileName.trim() || 'asset';
  if (normalized.length <= ASSET_TOAST_FILE_NAME_MAX_LENGTH) {
    return normalized;
  }

  const suffixLength = 16;
  const prefixLength = ASSET_TOAST_FILE_NAME_MAX_LENGTH - suffixLength - 3;
  return `${normalized.slice(0, prefixLength)}...${normalized.slice(
    -suffixLength,
  )}`;
}

function getAssetToastLabelInput(fileNames: readonly string[]): {
  fileName: string;
  remainingCount: number;
} {
  return {
    fileName: truncateAssetToastFileName(fileNames[0] ?? 'asset'),
    remainingCount: Math.max(0, fileNames.length - 1),
  };
}

type EditorEntry =
  | {
      name: string;
      editorView: ReturnType<typeof createEditor>;
      /** Exact source retained while the loaded document remains unchanged. */
      loadedDoc: EditorView['state']['doc'];
      loadedMarkdown: string;
      removeFocusListener: () => void;
      wsPath: string;
    }
  | { name: string; status: 'failed'; error: Error; wsPath: string }
  | {
      name: string;
      status: 'pending';
      /** Mount that owns this pending load; see `loadSequence`. */
      loadToken: number;
      wsPath: string;
    };

type ReadyEditorEntry = Extract<EditorEntry, { editorView: unknown }>;

/**
 * Manages ProseMirror editor instances and state. This is the ProseMirror
 * implementation of the `editorEngine` slot; everything outside this package
 * consumes it through `EditorEngineContract`.
 */
export class PmEditorService
  extends BaseService
  implements EditorEngineContract
{
  static deps = [
    'fileSystem',
    'navigation',
    'noteSnapshot',
    'workbenchState',
    'workspaceOps',
    'workspaceState',
  ] as const;

  public readonly engineId = 'prosemirror';

  public readonly extensions: ReturnType<typeof setupExtensions>;

  /**
   * wsPaths whose stored Markdown does not survive a parse/serialize round
   * trip. Saving an edit to such a note reformats content the user never
   * touched, so the editor surface shows a fidelity notice while one of
   * these notes is open. Checked once per editor load and cleared on
   * unmount.
   */
  $roundTripWarnings = atom<ReadonlySet<string>>(EMPTY_WS_PATH_SET);

  /**
   * Live word/character stats for the currently mounted note editor.
   */
  $activeDocStats = atom<{
    wsPath: string;
    stats: TextStats;
  } | null>(null);

  /**
   * Live frontmatter YAML for the currently mounted note editor.
   */
  $activeFrontmatter = atom<ActiveFrontmatter | null>(null);

  private saveQueue: EditorSaveQueue;
  private readonly saveCoordinator: EditorSaveCoordinator;
  private readonly canFocusEditorCheck: (() => boolean) | undefined;
  private pendingHeading: { fragment: string; wsPath: string } | undefined;
  private rememberedCursors = new Map<string, number>();
  private lastActiveEditorView: EditorView | undefined;
  /** Bumps on each collab prepare so a stale connect cannot become active. */
  private collabEpoch = 0;
  /** Providers still inside `connect()` — not yet in the active session. */
  private inflightCollab = new Set<{
    provider: SupabaseFileYjsProvider;
    ydoc: Y.Doc;
  }>();

  private editors = new Map<HTMLElement, EditorEntry>();

  /**
   * Identifies the newest `mountEditor` call. A load keeps checking that its
   * own token still owns the pending entry, so a remount (for example React
   * StrictMode's mount → unmount → mount cycle) cannot have the stale load
   * register a collab-less editor over the newer mount's pending slot.
   */
  private loadSequence = 0;

  /**
   * Markdown parse/serialize loaders keyed by editor schema. Every mounted
   * editor builds its own `Schema` instance, and parsing creates nodes bound
   * to the loader's schema — feeding nodes from one editor's schema into
   * another editor's document makes ProseMirror's replace fitting silently
   * drop the content. Keying by schema keeps each loader usable only with
   * the views it belongs to.
   */
  private markdownBySchema = new WeakMap<
    Schema,
    ReturnType<typeof markdownLoader>
  >();

  private handledExternalChangeSequence = 0;
  /**
   * Tells `onDocChange` that the doc change it is seeing was produced by an
   * external sync (not the user) and must not be re-saved — writing it back
   * would bump the file's mtime and make sync tools churn on their own change.
   *
   * Contract: a plain boolean is enough only because the saveDoc plugin's
   * `view.update` (and therefore `onDocChange`) runs synchronously inside
   * `view.dispatch`, which `replaceEditorContent` wraps. If the save pipeline
   * ever defers off the dispatch (debounce, microtask), this flag must move
   * onto the transaction itself (a meta read from plugin state).
   */
  private applyingExternalSync = false;
  private staleExternalContentWsPaths = new Set<string>();

  /**
   * Reconciles open editors with externally changed files. Owns sequencing,
   * coalescing, and content-stability policy; this service only supplies the
   * narrow host surface below.
   */
  private externalContentSync = new ExternalContentSync(
    {
      getViews: (wsPath) =>
        [...this.readyEditors()]
          .filter((editor) => editor.wsPath === wsPath)
          .map((editor) => editor.editorView),
      getMountedWsPaths: (wsName) => [
        ...new Set(
          [...this.readyEditors()]
            .filter(
              (editor) =>
                wsName === undefined ||
                WsPath.safeParse(editor.wsPath).data?.wsName === wsName,
            )
            .map((editor) => editor.wsPath),
        ),
      ],
      hasPendingSaves: (wsPath) => this.saveQueue.hasPendingOrFailed(wsPath),
      readFileAsText: (wsPath) =>
        this.dependencies.fileSystem.readFileAsText(wsPath, {
          signal: this.abortSignal,
        }),
      getMarkdown: (schema) => this.getMarkdown(schema),
      getSaveProjection: (view) =>
        serializeEditorDocumentForSave(
          this.getMarkdown(view.state.schema),
          view.state.doc,
        ),
      getRetainedSource: (view) => this.getRetainedSource(view),
      replaceContent: (args) => this.replaceEditorContent(args),
      onStaleContentRefused: (wsPath) => {
        this.showStaleExternalContentToast(wsPath);
      },
      onContentReconciled: (wsPath) => {
        this.dismissStaleExternalContentToast(wsPath);
      },
      logger: this.logger,
    },
    this.abortSignal,
  );

  constructor(
    context: BaseServiceContext,
    private dependencies: {
      fileSystem: FileSystemService;
      navigation: NavigationService;
      noteSnapshot: NoteSnapshotService;
      workbenchState: WorkbenchStateService;
      workspaceOps: WorkspaceOpsService;
      workspaceState: WorkspaceStateService;
    },
    config: PmEditorServiceConfig,
  ) {
    super(SERVICE_NAME.pmEditorService, context, dependencies);

    this.saveCoordinator = config.saveCoordinator;
    this.canFocusEditorCheck = config.canFocusEditor;
    this.extensions = setupExtensions(
      this.logger,
      (href, view) => this.openLink(view, href),
      {
        onActivate: (view, attrs) => {
          void this.openWikiLink(view, attrs.target);
        },
        resolveTarget: (attrs, state) => {
          const editor = this.getEditorEntryByView(
            (view) => view.state === state,
          );
          if (!editor) return false;
          const current = WsPath.safeParse(editor.wsPath).data?.asFile();
          if (!current) return false;
          return Boolean(
            resolveWikiLinkTarget(
              current,
              attrs.target,
              this.getWikiLinkIndex(current.wsName),
            ),
          );
        },
        unresolvedAriaLabel: ({ displayText }) =>
          t.app.editor.wikiLink.unresolvedLabel({ label: displayText }),
      },
      {
        storeFiles: (view, files, signal) =>
          this.storeAssetFiles(view, files, signal),
        cleanupStoredFiles: (assets) => {
          void this.cleanupStoredAssetFiles(assets);
        },
        resolveAssetReference: (view, target) =>
          this.resolveAssetReference(view, target),
      },
    );
    this.saveQueue = new EditorSaveQueue(
      async (wsPath, doc) => {
        const fileName = WsPath.assertFile(wsPath).fileName;
        await this.dependencies.fileSystem.writeFile(
          wsPath,
          new File([doc], fileName, {
            type: 'text/plain',
          }),
        );
        this.dismissStaleExternalContentToast(wsPath);
        // Markdown landed in Postgres — push Yjs immediately so another origin
        // does not reopen a stale collab snapshot while this badge says saved.
        // (The save pill ignores collab 'saving' so this does not flicker UI.)
        const collab = getActiveCollabSession();
        const parsed = WsPath.safeParse(wsPath).data;
        if (collab && parsed && collab.filePath === parsed.path) {
          void collab.provider.flushPersist();
        }
      },
      this.emitAppError,
      this.saveCoordinator,
      {
        onQueued: (wsPath, doc) => {
          void this.dependencies.noteSnapshot.persistUnsavedDraft(wsPath, doc);
        },
        onClean: (wsPath) => {
          void this.dependencies.noteSnapshot.clearUnsavedDraft(wsPath);
        },
      },
      { debounceMs: EDITOR_SAVE_DEBOUNCE_MS },
    );
  }

  hookMount() {
    this.addCleanup(() => {
      // Destroy all editor views
      for (const [_domNode, editor] of this.editors) {
        if ('editorView' in editor) {
          editor.removeFocusListener();
          editor.editorView.destroy();
        }
      }
      this.editors.clear();
      this.lastActiveEditorView = undefined;
      this.rememberedCursors.clear();
      for (const wsPath of [...this.staleExternalContentWsPaths]) {
        this.dismissStaleExternalContentToast(wsPath);
      }
    });
    this.addCleanup(
      this.store.sub(this.dependencies.navigation.$routeInfo, () => {
        const pending = this.pendingHeading;
        const routeInfo = this.store.get(
          this.dependencies.navigation.$routeInfo,
        );
        if (
          pending &&
          (routeInfo.route !== 'editor' ||
            routeInfo.payload.wsPath !== pending.wsPath)
        ) {
          this.pendingHeading = undefined;
        }
      }),
    );
    this.addCleanup(
      this.store.sub($pendingPresenceJump, () => {
        this.tryApplyPendingPresenceJump();
      }),
    );
    this.addCleanup(
      this.store.sub(this.dependencies.workspaceState.$noteWsPaths, () => {
        for (const editor of this.readyEditors()) {
          editor.editorView.dispatch(
            editor.editorView.state.tr.setMeta(
              'wiki-link-targets-changed',
              true,
            ),
          );
        }
      }),
    );
    this.addCleanup(
      this.store.sub(this.dependencies.fileSystem.$fileRenameEvent, () => {
        const event = this.store.get(
          this.dependencies.fileSystem.$fileRenameEvent,
        );
        if (event) {
          this.handleFileRename(event.oldWsPath, event.wsPath, event.external);
        }
      }),
    );
    this.addCleanup(
      this.store.sub(this.dependencies.fileSystem.$fileDeleteEvent, () => {
        const event = this.store.get(
          this.dependencies.fileSystem.$fileDeleteEvent,
        );
        if (event) {
          this.handleFileDelete(event.wsPath);
        }
      }),
    );
    this.addCleanup(
      this.store.sub(
        this.dependencies.fileSystem.$externalFileChangeEvent,
        () => {
          const event = this.store.get(
            this.dependencies.fileSystem.$externalFileChangeEvent,
          );
          if (!event || event.sequence <= this.handledExternalChangeSequence) {
            return;
          }
          this.handledExternalChangeSequence = event.sequence;
          if (event.type === 'file-delete') {
            this.handleFileDelete(event.wsPath);
          }
          this.externalContentSync.handleEvent(event);
        },
      ),
    );
  }

  private handleFileRename(
    oldWsPath: string,
    newWsPath: string,
    external: boolean,
  ): void {
    this.dismissStaleExternalContentToast(oldWsPath);
    // A local rename drains this queue before moving the file. A rename from
    // another tab has no such guarantee: keep a pending/failed editor on its
    // old path so its only unsaved body stays mounted and fails visibly rather
    // than being retargeted over the renamed file.
    if (external && this.saveQueue.hasPendingOrFailed(oldWsPath)) {
      return;
    }
    this.saveQueue.relocate(oldWsPath, newWsPath);

    for (const [domNode, editor] of this.editors) {
      if (editor.wsPath !== oldWsPath) {
        continue;
      }
      this.editors.set(domNode, { ...editor, wsPath: newWsPath });
    }
  }

  /**
   * After a path is deleted, drop collab/save/draft state without flushing.
   * Teardown flush would rewrite path-keyed ydoc that storage just purged.
   */
  private handleFileDelete(wsPath: string): void {
    this.dismissStaleExternalContentToast(wsPath);
    this.saveQueue.discard(wsPath);
    void this.dependencies.noteSnapshot.clearUnsavedDraft(wsPath);

    const parsed = WsPath.safeParse(wsPath).data;
    const active = getActiveCollabSession();
    if (
      active &&
      parsed &&
      active.filePath === parsed.path
    ) {
      abandonActiveCollabSession();
      this.store.set($collabPeers, []);
      this.store.set($collabStatus, null);
      this.store.set($collabYdoc, null);
      this.store.set($collabCommentThreads, []);
    }
  }

  mountEditor({
    domNode,
    wsPath,
    name,
    focus = true,
    readOnly = false,
  }: {
    domNode: HTMLElement;
    wsPath: string;
    name: string;
    focus?: boolean;
    readOnly?: boolean;
  }) {
    if (this.editors.has(domNode)) {
      return () => this.unmountEditor(domNode);
    }

    const wsPathObj = WsPath.fromString(wsPath);
    wsPathObj.assertMarkdown();

    // Mark this editor as pending. The token ties the pending slot to this
    // specific mount so a superseded load can never claim it.
    const loadToken = ++this.loadSequence;
    this.editors.set(domNode, { name, status: 'pending', loadToken, wsPath });

    void this.loadEditor({
      domNode,
      focus,
      loadToken,
      name,
      readOnly,
      wsPath,
    });

    return () => this.unmountEditor(domNode);
  }

  private async loadEditor({
    domNode,
    focus,
    loadToken,
    name,
    readOnly,
    wsPath,
  }: {
    domNode: HTMLElement;
    focus: boolean;
    loadToken: number;
    name: string;
    readOnly: boolean;
    wsPath: string;
  }): Promise<void> {
    // Markdown, mtime, and collab connect are independent — run together so
    // cloud notes are not blocked on three serial round-trips before paint.
    const collabPromise = this.prepareCollab(wsPath);
    try {
      const contentPromise =
        this.dependencies.fileSystem.readFileAsText(wsPath);
      const mtimePromise = this.dependencies.fileSystem
        .fileStat(wsPath)
        .then((stat) => stat.mtime)
        .catch(() => null as number | null);

      const [content, markdownMtimeMs, collab] = await Promise.all([
        contentPromise,
        mtimePromise,
        collabPromise,
      ]);
      if (!this.isEditorPending(domNode, wsPath, loadToken)) {
        this.discardOrphanCollab(collab);
        return;
      }

      // Prefer a retained emergency draft over storage when they differ, so a
      // crash or failed write cannot silently drop the user's latest edits.
      // Collab notes use Yjs as the shared source of truth — skip drafts there.
      let defaultContent = content ?? '';
      let recoveredDraft = false;
      if (!collab) {
        const draft =
          await this.dependencies.noteSnapshot.getUnsavedDraft(wsPath);
        if (draft && draft.content !== defaultContent) {
          defaultContent = draft.content;
          recoveredDraft = true;
        }
      }

      const editorView = createEditor({
        defaultContent,
        store: this.store as Store,
        domNode,
        readOnly,
        onDocChange: (doc) => {
          const currentWsPath = this.editors.get(domNode)?.wsPath ?? wsPath;
          const mounted = this.editors.get(domNode);
          if (mounted && 'editorView' in mounted) {
            this.publishActiveFrontmatter(
              currentWsPath,
              mounted.editorView.state.doc,
            );
          }
          if (this.applyingExternalSync) {
            // External sync applying disk content — must not be re-saved;
            // see applyingExternalSync.
            return;
          }
          this.saveQueue.enqueue(currentWsPath, doc);
        },
        onStatsChange: (stats) => {
          const currentWsPath = this.editors.get(domNode)?.wsPath ?? wsPath;
          this.store.set(this.$activeDocStats, {
            wsPath: currentWsPath,
            stats,
          });
        },
        extensions: this.extensions,
        nodeViews: {
          image: createLocalImageNodeView({
            currentWsPath: wsPath,
            fileSystem: this.dependencies.fileSystem,
            logger: this.logger,
            onOpen: (src, alt) => {
              this.store.set($imageLightbox, { src, alt });
            },
          }),
        },
        collab: collab
          ? {
              fragment: collab.provider.getXmlFragment(),
              awareness: collab.provider.awareness,
              readOnly: collab.readOnly,
              markdownMtimeMs,
              yjsUpdatedAtMs: collab.provider.getServerUpdatedAtMs(),
              yjsDirty: collab.provider.isDirty(),
            }
          : undefined,
      });

      if (!this.isEditorPending(domNode, wsPath, loadToken)) {
        editorView.destroy();
        this.discardOrphanCollab(collab);
        return;
      }

      // If open reconciled Markdown into a stale Yjs fragment, push that
      // correction to Postgres before another origin can reload the old snapshot.
      if (collab?.provider.isDirty()) {
        void collab.provider.flushPersist();
      }

      if (recoveredDraft) {
        // Opening with draft content does not fire onDocChange; queue a write
        // so the recovered body becomes durable without waiting for a keystroke.
        this.saveQueue.enqueue(wsPath, defaultContent);
        this.saveQueue.flushDebouncedSaves(wsPath);
        toast.success(t.app.toasts.unsavedDraftRecovered, {
          duration: 6_000,
        });
      }

      const handleFocusIn = () => {
        this.lastActiveEditorView = editorView;
      };
      editorView.dom.addEventListener('focusin', handleFocusIn);
      this.editors.set(domNode, {
        name,
        editorView,
        loadedDoc: editorView.state.doc,
        loadedMarkdown: content ?? '',
        removeFocusListener: () => {
          editorView.dom.removeEventListener('focusin', handleFocusIn);
        },
        wsPath,
      });
      this.publishActiveFrontmatter(wsPath, editorView.state.doc);
      this.checkRoundTripFidelity({
        content: content ?? '',
        editorView,
        wsPath,
      });
      editorView.dispatch(
        editorView.state.tr.setMeta('wiki-link-targets-changed', true),
      );
      if (collab) {
        setCommentThreads(this.store.get($collabCommentThreads), collab.userId)(
          editorView.state,
          editorView.dispatch,
        );
      }
      if (focus && this.canFocusEditor()) {
        editorView.focus();
      }
      if (collab) {
        this.tryApplyPendingPresenceJump();
      }
      const pendingHeading = this.pendingHeading;
      if (pendingHeading?.wsPath === wsPath) {
        const { fragment } = pendingHeading;
        this.pendingHeading = undefined;
        this.navigateToHeading(editorView, fragment);
      } else {
        if (pendingHeading) {
          this.pendingHeading = undefined;
        }
        this.restoreCursor(editorView, wsPath);
      }
    } catch (cause) {
      void collabPromise
        .then((collab) => this.discardOrphanCollab(collab))
        .catch(() => {});

      const editorEntry = this.editors.get(domNode);
      if (!editorEntry || 'editorView' in editorEntry) {
        return;
      }

      const error = cause instanceof Error ? cause : new Error(String(cause));
      if (this.pendingHeading?.wsPath === wsPath) {
        this.pendingHeading = undefined;
      }
      this.editors.set(domNode, { name, status: 'failed', error, wsPath });
      this.logger.error('Unable to load note', error);
      this.emitAppError(
        createAppError('error::editor:load-failed', 'Unable to load note', {
          error,
          wsPath,
        }),
      );
    }
  }

  private isEditorPending(
    domNode: HTMLElement,
    wsPath: string,
    loadToken: number,
  ): boolean {
    const entry = this.editors.get(domNode);
    return Boolean(
      entry &&
        !('editorView' in entry) &&
        entry.status === 'pending' &&
        entry.loadToken === loadToken &&
        entry.wsPath === wsPath,
    );
  }

  private discardOrphanCollab(
    collab: {
      provider: SupabaseFileYjsProvider;
      ydoc: Y.Doc;
    } | null,
  ) {
    if (!collab) return;
    this.inflightCollab.delete(collab);
    const active = getActiveCollabSession();
    if (active?.provider === collab.provider) {
      // Cancelled before paint — do not flush path-keyed ydoc back to PB.
      abandonActiveCollabSession();
      return;
    }
    collab.provider.destroy({ persist: false });
    collab.ydoc.destroy();
  }

  private abortInflightCollab() {
    for (const collab of this.inflightCollab) {
      collab.provider.destroy({ persist: false });
      collab.ydoc.destroy();
    }
    this.inflightCollab.clear();
  }

  private async prepareCollab(wsPath: string): Promise<{
    provider: SupabaseFileYjsProvider;
    ydoc: Y.Doc;
    readOnly: boolean;
    userId: string | null;
  } | null> {
    if (!isSupabaseConfigured()) return null;
    const parsed = WsPath.safeParse(wsPath).data;
    if (!parsed) return null;
    const info = await this.dependencies.workspaceOps.getWorkspaceInfo(
      parsed.wsName,
    );
    if (
      info?.type !== WORKSPACE_STORAGE_TYPE.Supabase ||
      typeof info.metadata.workspaceId !== 'string'
    ) {
      return null;
    }

    const epoch = ++this.collabEpoch;
    this.abortInflightCollab();
    const workspaceId = info.metadata.workspaceId;
    const filePath = parsed.path;
    const [membership, user] = await Promise.all([
      getMyMembership(workspaceId),
      getUser(),
    ]);
    if (epoch !== this.collabEpoch) return null;
    const readOnly = membership?.role === 'viewer';

    // Same-path replace (delete then recreate, or remount) must not flush the
    // previous session — that rewrite races createFile's ydoc purge.
    const previous = getActiveCollabSession();
    if (
      previous &&
      previous.workspaceId === workspaceId &&
      previous.filePath === filePath
    ) {
      abandonActiveCollabSession();
    } else {
      setActiveCollabSession(null);
    }
    this.store.set($collabPeers, []);
    this.store.set($collabStatus, 'connecting');
    this.store.set($collabCommentThreads, []);

    const ydoc = new Y.Doc();
    const provider = new SupabaseFileYjsProvider(ydoc, workspaceId, filePath, {
      readOnly,
      user: user
        ? {
            id: user.id,
            email: user.email,
            displayName:
              user.email ||
              (user.user_metadata?.display_name as string | undefined) ||
              undefined,
          }
        : null,
      onStatus: (status) => {
        if (epoch !== this.collabEpoch) return;
        this.store.set($collabStatus, status);
      },
    });
    const inflight = { provider, ydoc };
    this.inflightCollab.add(inflight);

    const collabUserId = user?.id ?? null;

    const pushCommentThreads = (threads: CommentThread[]) => {
      if (epoch !== this.collabEpoch) return;
      this.store.set($collabCommentThreads, threads);
      for (const editor of this.readyEditors()) {
        if (editor.wsPath !== wsPath) continue;
        setCommentThreads(threads, collabUserId)(
          editor.editorView.state,
          editor.editorView.dispatch,
        );
      }
    };
    const unsubPresence = provider.onPresence((peers) => {
      if (epoch !== this.collabEpoch) return;
      this.store.set($collabPeers, peers);
      this.tryApplyPendingPresenceJump();
    });
    const unsubComments = observeComments(ydoc, pushCommentThreads);

    try {
      await provider.connect();
    } catch (e) {
      this.logger.warn('collab connect failed', e);
      unsubPresence();
      unsubComments();
      this.inflightCollab.delete(inflight);
      provider.destroy();
      ydoc.destroy();
      if (epoch === this.collabEpoch) {
        this.store.set($collabStatus, null);
        this.store.set($collabYdoc, null);
      }
      return null;
    }

    if (epoch !== this.collabEpoch) {
      unsubPresence();
      unsubComments();
      this.inflightCollab.delete(inflight);
      provider.destroy();
      ydoc.destroy();
      return null;
    }

    this.inflightCollab.delete(inflight);
    const session = {
      provider,
      ydoc,
      workspaceId,
      filePath,
      mentionCandidates: [] as MentionCandidate[],
      unsubPresence,
      unsubComments,
    };
    this.store.set($collabYdoc, ydoc);
    setActiveCollabSession(session);

    // Mentions are not required to open the note — load them in the background.
    void (async () => {
      try {
        const members = await listMembers(workspaceId);
        if (epoch !== this.collabEpoch) return;
        const profiles = await fetchProfilesByIds(
          members.map((m) => m.user_id),
        );
        if (epoch !== this.collabEpoch) return;
        session.mentionCandidates = members
          .map((member) =>
            normalizeMentionCandidate({
              id: member.user_id,
              display_name: profiles[member.user_id]?.display_name ?? null,
              email: profiles[member.user_id]?.email ?? null,
            }),
          )
          .filter((c): c is MentionCandidate => Boolean(c));
      } catch (e) {
        this.logger.warn('mention candidates failed to load', e);
      }
    })();

    return { provider, ydoc, readOnly, userId: collabUserId };
  }

  private unmountEditor(domNode: HTMLElement) {
    const editor = this.editors.get(domNode);
    if (editor && 'editorView' in editor) {
      if (this.lastActiveEditorView === editor.editorView) {
        this.lastActiveEditorView = undefined;
      }
      const position = getRememberedCursorPosition(
        editor.editorView.state.selection,
      );
      if (position === undefined) {
        this.rememberedCursors.delete(editor.wsPath);
      } else {
        this.rememberedCursors.set(editor.wsPath, position);
      }
      editor.removeFocusListener();
      editor.editorView.destroy();
    }
    this.editors.delete(domNode);
    if (editor) {
      this.setRoundTripWarning(editor.wsPath, false);
      if (
        'editorView' in editor &&
        ![...this.readyEditors()].some(
          (mountedEditor) => mountedEditor.wsPath === editor.wsPath,
        )
      ) {
        this.dismissStaleExternalContentToast(editor.wsPath);
      }
      const activeStats = this.store.get(this.$activeDocStats);
      if (activeStats?.wsPath === editor.wsPath) {
        const stillOpen = [...this.editors.values()].some(
          (mounted) =>
            'editorView' in mounted && mounted.wsPath === editor.wsPath,
        );
        if (!stillOpen) {
          this.store.set(this.$activeDocStats, null);
        }
      }
      const activeFrontmatter = this.store.get(this.$activeFrontmatter);
      if (activeFrontmatter?.wsPath === editor.wsPath) {
        const stillOpen = [...this.editors.values()].some(
          (mounted) =>
            'editorView' in mounted && mounted.wsPath === editor.wsPath,
        );
        if (!stillOpen) {
          this.store.set(this.$activeFrontmatter, null);
        }
      }
    }
    if (this.editors.size === 0) {
      this.collabEpoch += 1;
      this.abortInflightCollab();
      setActiveCollabSession(null);
      this.store.set($collabPeers, []);
      this.store.set($collabStatus, null);
      this.store.set($collabYdoc, null);
      this.store.set($collabCommentThreads, []);
    }
  }

  private restoreCursor(editorView: EditorView, wsPath: string): void {
    const position = this.rememberedCursors.get(wsPath);
    if (position === undefined) {
      return;
    }
    const selection = resolveRememberedCursor(editorView.state.doc, position);
    if (!selection) {
      return;
    }
    editorView.dispatch(
      editorView.state.tr.setSelection(selection).scrollIntoView(),
    );
    // Restoring a cursor inside a Markdown link is navigation state, not a
    // user interaction with that link. Keep the cursor without reopening the
    // floating link editor, which can otherwise cover content after returning
    // to a note.
    this.extensions.linkMenu.command.dismissLinkMenu()(
      editorView.state,
      editorView.dispatch,
      editorView,
    );
  }

  /**
   * After Cmd/Ctrl-clicking a presence avatar, navigate lands on the peer's
   * note; once collab awareness is up, move the local caret to their cursor.
   */
  private tryApplyPendingPresenceJump(): void {
    const pending = this.store.get($pendingPresenceJump);
    if (!pending) return;
    const session = getActiveCollabSession();
    if (!session || session.filePath !== pending.filePath) return;

    let targetClientId: number | null = null;
    session.provider.awareness.getStates().forEach((state, clientId) => {
      if (targetClientId !== null) return;
      const user = state?.user as { id?: string } | undefined;
      if (user?.id === pending.userId) {
        targetClientId = clientId;
      }
    });
    if (targetClientId === null) return;

    const awarenessState = session.provider.awareness
      .getStates()
      .get(targetClientId) as
      | { cursor?: { anchor: unknown; head: unknown } }
      | undefined;
    const cursor = awarenessState?.cursor;
    if (!cursor?.head) return;

    const targetEditors = [...this.readyEditors()].filter((editor) => {
      const parsed = WsPath.safeParseFile(editor.wsPath).data;
      return parsed?.path === pending.filePath;
    });
    const editor = targetEditors[0];
    if (!editor) return;

    const syncState = ySyncPluginKey.getState(editor.editorView.state) as
      | {
          binding?: {
            type: Y.XmlFragment;
            mapping: Parameters<typeof relativePositionToAbsolutePosition>[3];
          };
        }
      | undefined;
    const binding = syncState?.binding;
    if (!binding) return;

    const head = relativePositionToAbsolutePosition(
      session.ydoc,
      binding.type,
      Y.createRelativePositionFromJSON(cursor.head),
      binding.mapping,
    );
    if (head === null) return;

    const max = editor.editorView.state.doc.content.size;
    const pos = Math.max(0, Math.min(head, max));
    try {
      const selection = TextSelection.create(editor.editorView.state.doc, pos);
      editor.editorView.dispatch(
        editor.editorView.state.tr.setSelection(selection).scrollIntoView(),
      );
      editor.editorView.focus();
      this.store.set($pendingPresenceJump, null);
    } catch {
      // Selection can fail if the doc shrank mid-flight; keep pending for retry.
    }
  }

  /**
   * Verifies the freshly loaded document serializes back to the exact
   * source it was parsed from. A failure here never blocks the editor —
   * the user can still read and edit — but it flips the fidelity warning
   * so the reformat-on-save behavior is visible instead of silent.
   */
  private checkRoundTripFidelity({
    content,
    editorView,
    wsPath,
  }: {
    content: string;
    editorView: EditorView;
    wsPath: string;
  }): void {
    try {
      const serialized = this.getMarkdown(
        editorView.state.schema,
      ).serializer.serialize(editorView.state.doc);
      const preserved = isMarkdownRoundTripPreserved(content, serialized);
      if (!preserved) {
        this.logger.warn(
          `Markdown round-trip mismatch for ${wsPath}: saving an edit will reformat parts of this note`,
        );
      }
      this.setRoundTripWarning(wsPath, !preserved);
    } catch (error) {
      // If the serializer itself fails we cannot prove fidelity, which is
      // exactly what the warning communicates.
      this.logger.warn(
        `Unable to verify Markdown round-trip for ${wsPath}`,
        error,
      );
      this.setRoundTripWarning(wsPath, true);
    }
  }

  /**
   * The external sync confirmed the disk copy of `wsPath` differs but
   * refused to auto-apply it (fidelity, emptied-file, schema, or parse
   * refusal). Without this the user would keep looking at silently stale
   * content with only a console warning. The offered action loads the disk
   * version into this note's editors in place — deliberately not a UI reload,
   * which would tear down every editor and could strand another note's pending
   * or failed save.
   */
  private showStaleExternalContentToast(wsPath: string): void {
    const fileName = WsPath.safeParseFile(wsPath).data?.fileName ?? wsPath;
    this.staleExternalContentWsPaths.add(wsPath);
    toast.warning(t.app.toasts.externalChangeNotApplied({ fileName }), {
      id: staleExternalContentToastId(wsPath),
      duration: Number.POSITIVE_INFINITY,
      cancel: {
        label: t.app.common.dismiss,
        onClick: () => {},
      },
      action: {
        label: t.app.toasts.loadDiskVersion,
        onClick: () => {
          void this.loadDiskVersionIntoEditors(wsPath);
        },
      },
    });
  }

  private dismissStaleExternalContentToast(wsPath: string): void {
    if (!this.staleExternalContentWsPaths.delete(wsPath)) {
      return;
    }
    toast.dismiss(staleExternalContentToastId(wsPath));
  }

  /**
   * User-consented recovery for external changes the automatic sync
   * refused: replaces only this note's open editors with the current disk
   * content, then runs the same fidelity check as note loading. Unsaved
   * local edits still win — a note that became dirty since the refusal is
   * left alone (its save resolves the divergence).
   */
  async loadDiskVersionIntoEditors(wsPath: string): Promise<void> {
    const fileName = WsPath.safeParseFile(wsPath).data?.fileName ?? wsPath;
    try {
      const result = await this.externalContentSync.acceptDiskVersion(wsPath);
      if (result.unavailable) {
        this.logger.warn(
          `Disk version of ${wsPath} could not be read; keeping the editor content`,
        );
        this.dismissStaleExternalContentToast(wsPath);
        toast.error(t.app.toasts.externalDiskVersionUnavailable({ fileName }));
        return;
      }
      if (result.appliedCount === 0) {
        // Nothing was replaced — local edits reclaimed the note, or it was
        // closed. Keep the notice so the user still has a way back to disk
        // instead of silently leaving them on the older content.
        this.logger.warn(
          `Disk version of ${wsPath} was not applied; keeping the editor content`,
        );
        toast.error(t.app.toasts.externalDiskVersionNotApplied({ fileName }));
        return;
      }

      this.logger.info(
        `${wsPath}: loaded the disk version into ${result.appliedCount} open editor(s) at the user's request`,
      );
      // The fidelity notice is refreshed by replaceEditorContent, which every
      // applied view goes through.
      if (!result.retry) {
        this.dismissStaleExternalContentToast(wsPath);
      }
    } catch (error) {
      // Parsing or serializing the disk copy threw. The notice stays so the
      // user keeps a way back, but they must be told the action did nothing —
      // a deterministic parse failure will never succeed on retry.
      this.logger.warn(`Could not load the disk version of ${wsPath}`, error);
      toast.error(t.app.toasts.externalDiskVersionNotApplied({ fileName }));
    }
  }

  private setRoundTripWarning(wsPath: string, hasWarning: boolean): void {
    const current = this.store.get(this.$roundTripWarnings);
    if (current.has(wsPath) === hasWarning) {
      return;
    }
    const next = new Set(current);
    if (hasWarning) {
      next.add(wsPath);
    } else {
      next.delete(wsPath);
    }
    this.store.set(this.$roundTripWarnings, next);
  }

  /**
   * A freshly loaded note may contain Markdown the editor cannot serialize
   * byte-for-byte. While its document is unchanged, that exact source is what
   * must be preserved when external content replaces it — not a normalized
   * re-serialization.
   */
  private getRetainedSource(view: EditorView): string | undefined {
    for (const editor of this.readyEditors()) {
      if (editor.editorView === view && editor.loadedDoc === view.state.doc) {
        return editor.loadedMarkdown;
      }
    }
    return undefined;
  }

  /**
   * Whether this view may still be replaced from disk. Re-checked after every
   * await in `replaceEditorContent`, so it lives in one place: two hand-copied
   * condition chains had already drifted apart.
   */
  private checkReplaceable(
    view: EditorView,
    expectedDoc: EditorView['state']['doc'],
    wsPath: string,
  ): 'ok' | 'retry' | 'skipped' {
    if (
      this.abortSignal.aborted ||
      this.saveQueue.hasPendingOrFailed(wsPath) ||
      view.isDestroyed ||
      view.state.doc !== expectedDoc ||
      this.getEditorEntryByView(view)?.wsPath !== wsPath
    ) {
      return 'skipped';
    }
    // Replacing the doc mid-composition silently drops uncommitted input.
    return view.composing ? 'retry' : 'ok';
  }

  /**
   * The single lifecycle for replacing a mounted editor from disk. Snapshot
   * storage is asynchronous, so every safety condition is checked both before
   * and after it; in particular an IME composition that begins while the
   * snapshot is being stored must never be destroyed by the replacement.
   */
  private async replaceEditorContent({
    expectedDoc,
    preservableSource,
    sourceMarkdown,
    transaction,
    view,
    wsPath,
  }: {
    expectedDoc: EditorView['state']['doc'];
    preservableSource: string;
    sourceMarkdown: string;
    transaction: Transaction;
    view: EditorView;
    wsPath: string;
  }): Promise<'applied' | 'refused' | 'retry' | 'skipped'> {
    const before = this.checkReplaceable(view, expectedDoc, wsPath);
    if (before !== 'ok') {
      return before;
    }

    const preserved =
      await this.dependencies.noteSnapshot.preserveExternalOverwrite(
        wsPath,
        preservableSource,
      );
    if (preserved === 'retry') {
      return 'retry';
    }
    if (preserved === 'unpreservable') {
      // Applying disk content would destroy the editor's copy with no
      // recovery path, so keep it and tell the user instead.
      return 'refused';
    }

    const after = this.checkReplaceable(view, expectedDoc, wsPath);
    if (after !== 'ok') {
      return after;
    }

    this.applyingExternalSync = true;
    try {
      view.dispatch(transaction);
    } finally {
      this.applyingExternalSync = false;
    }

    const editor = this.getEditorEntryByView(view);
    if (editor?.wsPath === wsPath) {
      editor.loadedDoc = view.state.doc;
      editor.loadedMarkdown = sourceMarkdown;
    }
    // The newly loaded source has its own fidelity: external content may
    // normalize on save where the previous content did not, or vice versa.
    // Auto-applied content needs this as much as a user-requested load.
    this.checkRoundTripFidelity({
      content: sourceMarkdown,
      editorView: view,
      wsPath,
    });
    return 'applied';
  }

  getEditorLoadStatus(
    name: string,
  ):
    | { status: 'failed'; error: Error; wsPath: string }
    | { status: 'pending'; wsPath: string }
    | { status: 'ready'; wsPath: string }
    | { status: 'missing' } {
    for (const [_, editor] of this.editors) {
      if (editor.name !== name) {
        continue;
      }

      if ('editorView' in editor) {
        return { status: 'ready', wsPath: editor.wsPath };
      }

      if (editor.status === 'failed') {
        return {
          status: 'failed',
          error: editor.error,
          wsPath: editor.wsPath,
        };
      }

      return { status: 'pending', wsPath: editor.wsPath };
    }

    return { status: 'missing' };
  }

  hasPendingOrFailedSave(wsPath?: string): boolean {
    return this.saveQueue.hasPendingOrFailed(wsPath);
  }

  flushDebouncedSaves(wsPath?: string): void {
    this.saveQueue.flushDebouncedSaves(wsPath);
  }

  getSaveStatus(wsPath: string) {
    return this.saveQueue.getStatus(wsPath);
  }

  /**
   * The live collaborative session for the open Supabase note, or `null`
   * outside of collaboration. Primarily a diagnostics seam (`?debug=true`
   * tooling and E2E assertions): awareness/cursor state is otherwise only
   * reachable through React atoms.
   */
  getActiveCollabSession() {
    return getActiveCollabSession();
  }

  listFailedSaves() {
    return this.saveQueue.listFailedSaves();
  }

  getUnsavedBodies(): ReadonlyMap<string, string> {
    return this.saveCoordinator.latestBodies;
  }

  subscribeToSaveStatus(listener: () => void, wsPath?: string): () => void {
    return this.saveQueue.subscribe(listener, wsPath);
  }

  retryFailedSave(wsPath?: string): boolean {
    return this.saveQueue.retryFailed(wsPath);
  }

  getEditor(name: string) {
    for (const editor of this.readyEditors()) {
      if (editor.name === name) {
        return editor.editorView;
      }
    }
    return undefined;
  }

  setFrontmatterText(wsPath: string, text: string): boolean {
    for (const editor of this.readyEditors()) {
      if (editor.wsPath !== wsPath) {
        continue;
      }
      const ok = replaceFrontmatterText(editor.editorView, text);
      if (ok) {
        this.publishActiveFrontmatter(wsPath, editor.editorView.state.doc);
      }
      return ok;
    }
    return false;
  }

  private publishActiveFrontmatter(
    wsPath: string,
    doc: EditorView['state']['doc'],
  ) {
    this.store.set(this.$activeFrontmatter, {
      wsPath,
      text: readFrontmatterTextFromDoc(doc),
    });
  }

  private getEditorEntryByView(
    editorView:
      | ReturnType<typeof createEditor>
      | ((editorView: ReturnType<typeof createEditor>) => boolean),
  ): ReadyEditorEntry | undefined {
    for (const editor of this.editors.values()) {
      if (!('editorView' in editor)) {
        continue;
      }
      const matches =
        typeof editorView === 'function'
          ? editorView(editor.editorView)
          : editor.editorView === editorView;
      if (matches) {
        return editor;
      }
    }
    return undefined;
  }

  private async storeAssetFiles(
    editorView: ReturnType<typeof createEditor>,
    files: readonly File[],
    signal: AbortSignal,
  ): Promise<StoredMarkdownAsset[]> {
    const editor = this.getEditorEntryByView(editorView);
    if (!editor) {
      return [];
    }

    const preference = this.store.get(
      this.dependencies.workbenchState.$assetLocationPreference,
    );

    const inputToastLabel = getAssetToastLabelInput(
      files.map(displayNameForAsset),
    );
    let failedCount = 0;
    const failedAssetNames: string[] = [];
    let oversizedAsset: { fileName: string; maxFileSize: string } | undefined;
    const toastId = `editor-assets:${editor.wsPath}:${Date.now()}`;
    toast.loading(t.app.toasts.assetSaveInProgress(inputToastLabel), {
      id: toastId,
    });

    const stored = await storeWorkspaceAssetFiles({
      sourceWsPath: editor.wsPath,
      files,
      preference,
      fileSystem: this.dependencies.fileSystem,
      signal,
      onFileError: ({ error, file }) => {
        failedCount += 1;
        failedAssetNames.push(displayNameForAsset(file));
        if (isAppError(error)) {
          const appError = getAppErrorCause(error);
          if (appError?.name === 'error::file:size-too-large') {
            oversizedAsset ??= {
              fileName: truncateAssetToastFileName(appError.payload.fileName),
              maxFileSize: formatFileSize(appError.payload.maxFileSizeBytes),
            };
            this.logger.warn('Rejected oversized asset', error);
            this.emitAppError(error);
            return;
          }
        }
        this.logger.error('Unable to store pasted asset', error);
        this.emitAppError(
          createAppError(
            'error::editor:asset-write-failed',
            'Unable to store asset',
            {
              error,
              wsPath: editor.wsPath,
            },
          ),
        );
      },
    });
    const markdownAssets = stored.filter(
      (asset): asset is StoredMarkdownAsset => Boolean(asset.href),
    );
    if (signal.aborted) {
      toast.dismiss(toastId);
      return markdownAssets;
    }

    if (failedCount > 0) {
      if (failedCount === 1 && markdownAssets.length === 0 && oversizedAsset) {
        toast.error(t.app.toasts.assetTooLarge(oversizedAsset), {
          id: toastId,
          duration: 7000,
        });
        return markdownAssets;
      }

      const savedToastLabel =
        markdownAssets.length > 0
          ? getAssetToastLabelInput(markdownAssets.map((asset) => asset.label))
          : undefined;
      const failedToastLabel = getAssetToastLabelInput(failedAssetNames);
      toast.error(
        t.app.toasts.assetSavePartial({
          failedCount,
          failedFileName: failedToastLabel.fileName,
          failedRemainingCount: failedToastLabel.remainingCount,
          savedFileName: savedToastLabel?.fileName,
          savedRemainingCount: savedToastLabel?.remainingCount ?? 0,
        }),
        { id: toastId, duration: 5000 },
      );
    } else if (markdownAssets.length > 0) {
      const firstAsset = markdownAssets[0];
      const savedToastLabel = getAssetToastLabelInput(
        markdownAssets.map((asset) => asset.label),
      );
      toast.success(t.app.toasts.assetSaveSucceeded(savedToastLabel), {
        id: toastId,
        duration: 5000,
        action: firstAsset
          ? {
              label: t.app.toasts.openAsset,
              onClick: () => {
                this.dependencies.navigation.goWsFile(firstAsset.wsPath.wsPath);
                toast.dismiss(toastId);
              },
            }
          : undefined,
      });
    } else {
      toast.dismiss(toastId);
    }
    return markdownAssets;
  }

  private async cleanupStoredAssetFiles(
    assets: readonly StoredMarkdownAsset[],
  ): Promise<void> {
    await Promise.all(
      assets.map(async (asset) => {
        try {
          await this.dependencies.fileSystem.deleteFile(asset.wsPath.wsPath);
        } catch (cause) {
          const error =
            cause instanceof Error ? cause : new Error(String(cause));
          this.logger.warn('Unable to clean up unused stored asset', error);
        }
      }),
    );
  }

  private resolveAssetReference(
    view: EditorView,
    target: string,
  ): MarkdownAssetReference | undefined {
    const editor = [...this.editors.values()].find(
      (entry) => 'editorView' in entry && entry.editorView === view,
    );
    if (!editor || !('editorView' in editor)) {
      return undefined;
    }

    const currentWsPath = WsPath.safeParse(editor.wsPath).data?.asFile();
    if (!currentWsPath) {
      return undefined;
    }

    const assetWsPath = this.resolveExistingAssetWsPath(currentWsPath, target);
    if (!assetWsPath) {
      return undefined;
    }

    const href =
      relativeMarkdownAssetHref(currentWsPath, assetWsPath) ??
      workspaceRootMarkdownAssetHref(assetWsPath);
    if (!href) {
      return undefined;
    }

    return {
      href,
      isImage: getEmbeddableWorkspaceAssetKind(assetWsPath) === 'image',
      label: assetWsPath.fileName,
    };
  }

  private resolveExistingAssetWsPath(
    currentWsPath: WsFilePath,
    target: string,
  ): WsFilePath | undefined {
    const existingWsPaths = new Set(
      this.store
        .get(this.dependencies.workspaceState.$wsPaths)
        .map((path) => path.wsPath),
    );
    return resolveWorkspaceMarkdownAssetReferenceCandidates(
      currentWsPath,
      target,
    ).find((candidate) => existingWsPaths.has(candidate.wsPath));
  }

  /** Opens a web link externally or routes a relative Markdown link in-app. */
  openLink(editorView: ReturnType<typeof createEditor>, href: string): void {
    const editor = this.getEditorEntryByView(editorView);
    if (!editor) {
      return;
    }

    const target = normalizeStoredMarkdownLinkTarget(href);
    if (target?.kind === 'internal') {
      const wsPath = resolveInternalLink(editor.wsPath, target.href);
      if (wsPath) {
        const fragment = getInternalLinkHeading(target.href);
        if (wsPath === editor.wsPath) {
          if (fragment) {
            this.navigateToHeading(editorView, fragment);
          }
          return;
        }
        this.pendingHeading = fragment ? { fragment, wsPath } : undefined;
        this.dependencies.navigation.goWsPath(wsPath);
      }
      return;
    }

    if (this.openAssetLink(editorView, href, { includeMarkdown: true })) {
      return;
    }

    const externalTarget =
      target?.kind === 'external' ? target : normalizeLinkTarget(href);
    if (externalTarget?.kind === 'external') {
      window.open(externalTarget.href, '_blank', 'noopener,noreferrer');
    }
  }

  private openAssetLink(
    editorView: ReturnType<typeof createEditor>,
    href: string,
    options: { includeMarkdown?: boolean } = {},
  ): boolean {
    const editor = this.getEditorEntryByView(editorView);
    if (!editor) {
      return false;
    }

    const currentWsPath = WsPath.safeParse(editor.wsPath).data?.asFile();
    if (!currentWsPath) {
      return false;
    }

    const assetWsPath = this.resolveExistingAssetWsPath(currentWsPath, href);
    if (!assetWsPath) {
      return false;
    }

    if (assetWsPath.isMarkdown()) {
      if (!options.includeMarkdown) {
        return false;
      }
      this.dependencies.navigation.goWsPath(assetWsPath.wsPath);
      return true;
    }

    this.dependencies.navigation.go({
      route: 'asset',
      payload: { wsPath: assetWsPath.wsPath },
    });
    return true;
  }

  /** Opens an existing wiki target, or creates a safe missing Markdown target. */
  async openWikiLink(
    editorView: ReturnType<typeof createEditor>,
    target: string,
  ): Promise<void> {
    const editor = this.getEditorEntryByView(editorView);
    if (!editor) return;
    const current = WsPath.safeParse(editor.wsPath).data?.asFile();
    if (!current) return;
    const resolved = resolveWikiLinkTarget(
      current,
      target,
      this.getWikiLinkIndex(current.wsName),
    );
    if (resolved && resolved.wsPath !== editor.wsPath) {
      this.dependencies.navigation.goWsPath(resolved.wsPath);
      return;
    }

    if (resolved) {
      return;
    }

    const missingTarget = createMissingWikiLinkTarget(current, target);
    if (!missingTarget) {
      return;
    }

    try {
      await this.dependencies.fileSystem.createFile(
        missingTarget.wsPath,
        new File([''], missingTarget.fileName, {
          type: 'text/plain',
        }),
      );
      this.dependencies.navigation.goWsPath(missingTarget.wsPath);
    } catch (cause) {
      if (isAppError(cause)) {
        const appError = getAppErrorCause(cause);
        if (appError?.name === 'error::file:already-existing') {
          this.dependencies.navigation.goWsPath(missingTarget.wsPath);
          return;
        }
      }

      const error = cause instanceof Error ? cause : new Error(String(cause));
      this.logger.error('Unable to create missing wiki link target', error);
      this.emitAppError(
        createAppError(
          'error::file:invalid-note-path',
          'Unable to create linked note',
          {
            invalidWsPath: missingTarget.wsPath,
          },
        ),
      );
    }
  }

  private getWikiLinkIndex(wsName: string): WikiLinkIndex {
    const currentIndex = this.store.get(
      this.dependencies.workspaceState.$wikiLinkIndex,
    );
    if (currentIndex?.wsName === wsName) {
      return currentIndex;
    }
    const wsPaths = this.store.get(
      this.dependencies.workspaceState.$noteWsPaths,
    );
    return createWikiLinkIndex(wsPaths, wsName);
  }

  private navigateToHeading(
    editorView: ReturnType<typeof createEditor>,
    fragment: string,
  ): boolean {
    const headings: Array<{ pos: number; text: string }> = [];
    editorView.state.doc.descendants((node, pos) => {
      if (node.type.name === 'heading') {
        headings.push({ pos, text: node.textContent });
      }
    });
    const index = findHeadingIndexBySlug(
      headings.map(({ text }) => text),
      fragment,
    );
    const heading = index === undefined ? undefined : headings[index];
    if (!heading) {
      return false;
    }
    editorView.dispatch(
      editorView.state.tr
        .setSelection(
          TextSelection.near(editorView.state.doc.resolve(heading.pos + 1)),
        )
        .scrollIntoView(),
    );
    return true;
  }

  focusEditor() {
    if (!this.canFocusEditor()) {
      return;
    }
    this.getActiveEditorView()?.focus();
  }

  private canFocusEditor(): boolean {
    return this.canFocusEditorCheck?.() ?? true;
  }

  /** Dry-runs app-level editor actions against the live selection. */
  isActionAvailable(action: EditorAction): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    if (action.type === 'insert-table') {
      return this.extensions.table.command.insertTable()(view.state);
    }
    return this.extensions.heading.command.toggleHeading(action.level)(
      view.state,
    );
  }

  /** Toggles the block at the current selection to/from a heading. */
  toggleHeading(level: number): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    const handled = this.extensions.heading.command.toggleHeading(level)(
      view.state,
      view.dispatch,
      view,
    );
    if (handled) {
      view.focus();
    }
    return handled;
  }

  /** Inserts a starter table at the current selection. */
  insertTable(): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    const handled = this.extensions.table.command.insertTable()(
      view.state,
      view.dispatch,
      view,
    );
    if (handled) {
      // Replacing the focused block with the table drops DOM focus; restore
      // it so typing goes into the first cell.
      view.focus();
    }
    return handled;
  }

  /** Folds/unfolds the heading section at the current selection. */
  toggleHeadingCollapse(): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    return this.extensions.collapsibleHeading.command.toggleHeadingCollapse(
      view.state,
      view.dispatch,
    );
  }

  /** Folds every heading of the given level in the active editor. */
  collapseAllHeadings(level: number): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    return this.extensions.collapsibleHeading.command.collapseAllHeadingsAtLevel(
      level,
    )(view.state, view.dispatch);
  }

  /** Expands every folded heading section in the active editor. */
  uncollapseAllHeadings(): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    return this.extensions.collapsibleHeading.command.uncollapseAllHeadings(
      view.state,
      view.dispatch,
    );
  }

  /**
   * Serializes the current selection in the active editor to Markdown.
   *
   * Reuses the same serializer configuration as the save path so copied
   * content matches what would be written to disk. Returns null when there is
   * no active editor or the selection is empty.
   */
  getSelectionMarkdown(): string | null {
    const view = this.getActiveEditorView();
    if (!view) {
      return null;
    }
    const { from, to, empty } = view.state.selection;
    if (empty) {
      return null;
    }
    return this.getMarkdown(view.state.schema).serializer.serialize(
      view.state.doc.cut(from, to),
    );
  }

  /**
   * Parses `markdownText` and inserts the resulting rich content at the current
   * selection in the active editor, replacing any selected content. This is the
   * inverse of {@link getSelectionMarkdown} and uses the same loader as the save
   * path so round-tripping preserves Markdown fidelity.
   *
   * A single parsed paragraph is inserted inline so it merges into the current
   * paragraph; headings and other block content are inserted as blocks. Returns
   * false when there is no active editor or the text produces no insertable
   * content (so a selection is never silently deleted).
   */
  insertMarkdownAtSelection(markdownText: string): boolean {
    const view = this.getActiveEditorView();
    if (!view) {
      return false;
    }
    return this.insertMarkdownIntoView(view, markdownText);
  }

  /**
   * Binds a future Markdown insertion to the current editor state. Clipboard
   * reads may wait on a permission prompt; during that wait the user can switch
   * notes or move the selection, and the pending operation must not follow.
   */
  captureMarkdownInsertion(): ((markdownText: string) => boolean) | null {
    const view = this.getActiveEditorView();
    if (!view) {
      return null;
    }
    const capturedDoc = view.state.doc;
    const capturedSelection = view.state.selection;

    return (markdownText) => {
      if (
        view.isDestroyed ||
        !this.getEditorEntryByView(view) ||
        this.getActiveEditorView() !== view ||
        !view.state.doc.eq(capturedDoc) ||
        !view.state.selection.eq(capturedSelection)
      ) {
        return false;
      }
      return this.insertMarkdownIntoView(view, markdownText);
    };
  }

  private insertMarkdownIntoView(
    view: ReturnType<typeof createEditor>,
    markdownText: string,
  ): boolean {
    const markdown = this.getMarkdown(view.state.schema);
    let parsed: ReturnType<typeof markdown.parser.parse>;
    try {
      parsed = markdown.parser.parse(markdownText);
      // Refuse only when parsing dropped content the user can see. A byte
      // comparison would also refuse pure normalization (`*italic*` arriving
      // as `_italic_`, `* item` as `- item`), which rejects most Markdown
      // copied from anywhere else and loses nothing.
      if (
        !isMarkdownContentPreserved(markdownText, collectLinkTargets(parsed))
      ) {
        return false;
      }
    } catch {
      return false;
    }
    const inline =
      parsed.childCount === 1 && parsed.firstChild?.type.name === 'paragraph';
    const slice = inline
      ? parsed.slice(1, Math.max(1, parsed.content.size - 1))
      : parsed.slice(0, parsed.content.size);
    if (slice.size === 0) {
      return false;
    }
    // A slice the schema cannot place here replaces nothing. Reporting success
    // for that would drop the clipboard content without telling anyone, so the
    // caller has to hear about it.
    const tr = view.state.tr.replaceSelection(slice);
    if (!tr.docChanged) {
      return false;
    }
    view.dispatch(tr.scrollIntoView());
    view.focus();
    return true;
  }

  private getMarkdown(schema: Schema) {
    const existing = this.markdownBySchema.get(schema);
    if (existing) {
      return existing;
    }

    const markdown = markdownLoader(
      [...Object.values(this.extensions)],
      schema,
    );
    this.markdownBySchema.set(schema, markdown);
    return markdown;
  }

  /** Mounted editors whose view is created and not destroyed. */
  private *readyEditors(): Generator<ReadyEditorEntry> {
    for (const editor of this.editors.values()) {
      if ('editorView' in editor && !editor.editorView.isDestroyed) {
        yield editor;
      }
    }
  }

  private getActiveEditorView() {
    let fallback: ReturnType<typeof createEditor> | undefined;
    let lastActive: ReturnType<typeof createEditor> | undefined;
    for (const editor of this.editors.values()) {
      if ('editorView' in editor && !editor.editorView.isDestroyed) {
        if (editor.editorView.hasFocus()) {
          this.lastActiveEditorView = editor.editorView;
          return editor.editorView;
        }
        if (editor.editorView === this.lastActiveEditorView) {
          lastActive = editor.editorView;
        }
        fallback ??= editor.editorView;
      }
    }
    this.lastActiveEditorView = lastActive;
    return lastActive ?? fallback;
  }
}
